import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setUserDetail,
  setUserDetailError,
  setLoadingSmall,
  clearUserDetail,
} from 'store/usersSlice';
import { userService } from 'components/api/users';
import styles from './userDetails.module.scss';
import Loader from 'components/loader/loader';
import UserInfo from './tabs/UserInfo';
import UserFinances from './tabs/UserFinances';
import UserBonuses from './tabs/UserBonuses';
import DailyWheelSettings from './tabs/DailyWheelSettings';
// import UserWheelSettings from './tabs/UserWheelSettings';
import UserReferrals from './tabs/UserReferrals';
import UserFreeSpins from './tabs/UserFreeSpins';
import UserGameHistory from './tabs/UserGameHistory';
import UserNotes from './tabs/UserNotes';
import { ChevronLeft } from 'lucide-react';

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userDetail, isLoadingSmall, error } = useAppSelector((state) => state.users);
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useMediaQuery('(max-width:768px)');

  const fetchUser = async (userId: string) => {
    try {
      dispatch(setLoadingSmall(true));
      const response = await userService.getUserById(userId);
      dispatch(setUserDetail(response.data));
      dispatch(setUserDetailError(null));
    } catch (err: any) {
      dispatch(
        setUserDetailError(err.response?.data?.message || err.message || 'Failed to fetch user'),
      );
    } finally {
      dispatch(setLoadingSmall(false));
    }
  };

  useEffect(() => {
    if (id) {
      fetchUser(id);
    }
    return () => {
      dispatch(clearUserDetail());
    };
  }, [id]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0 && id) {
      fetchUser(id);
    }
  };

  if (isLoadingSmall && !userDetail) {
    return (
      <div className={styles.userDetailsContainer}>
        <div className={styles.loadingWrapper}>
          <Loader />
          <Typography>Loading user details...</Typography>
        </div>
      </div>
    );
  }

  if (error || !userDetail) {
    return (
      <div className={styles.userDetailsContainer}>
        <div className={styles.errorWrapper}>
          <Typography color="error">{error || 'User not found'}</Typography>
          <button className={styles.backBtn} onClick={() => navigate('/clients')}>
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.userDetailsContainer}>
      <Box className={styles.header}>
        <Button
          startIcon={<ChevronLeft size={20} />}
          onClick={() => navigate('/clients')}
          className={styles.backBtn}
        >
          Back to Clients
        </Button>
      </Box>

      <Box className={styles.mainLayout}>
        <Box className={styles.tabsWrapper}>
          <Tabs
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : undefined}
            value={activeTab}
            onChange={handleTabChange}
            className={styles.tabs}
            TabIndicatorProps={{
              className: isMobile ? styles.horizontalIndicator : styles.tabIndicator,
            }}
            sx={{ borderRight: 0 }}
          >
            <Tab label="Info" className={styles.tab} />
            <Tab label="Finances" className={styles.tab} />
            <Tab label="Bonuses" className={styles.tab} />
            <Tab label="Daily wheel" className={styles.tab} />

            {/* <Tab label="Wheel settings" className={styles.tab} /> */}
            <Tab label="Referrals" className={styles.tab} />
            <Tab label="Free spins" className={styles.tab} />
            <Tab label="Game history" className={styles.tab} />
            <Tab label="Notes" className={styles.tab} />
          </Tabs>
        </Box>

        <Box className={styles.contentWrapper}>
          {activeTab === 0 && <UserInfo user={userDetail} />}
          {activeTab === 1 && <UserFinances />}
          {activeTab === 2 && <UserBonuses userId={id!} />}
          {activeTab === 3 && <DailyWheelSettings userId={id!} />}
          {/* {activeTab === 3 && <UserWheelSettings userId={id!} />} */}
          {activeTab === 4 && <UserReferrals userId={id!} />}
          {activeTab === 5 && <UserFreeSpins userId={id!} siteId={userDetail.site.id} />}
          {activeTab === 6 && <UserGameHistory userId={id!} siteId={userDetail.site.id} />}
          {activeTab === 7 && <UserNotes userId={id!} />}
        </Box>
      </Box>
    </div>
  );
};

export default UserDetails;
